import React, { useState } from 'react';
import { sejours } from '../data/mock';
import { Link } from 'react-router-dom';
import { Users, Clock, ArrowRight, Phone, ChevronDown, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const Sejours = () => {
  // État pour gérer quel séjour est déplié (par son ID)
  const [expandedId, setExpandedId] = useState(null);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/sejour.jpg')" }}
        >
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Les Séjours</h1>
          <p className="text-xl md:text-2xl text-gray-200 leading-relaxed">
            Trouvez votre séjour idéal en combinant hébergement et activités de pleine nature
          </p>
        </div>
      </section>

      {/* Sejours Grid */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {sejours.map((sejour) => (
              <div
                key={sejour.id}
                className="group bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 flex flex-col"
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={sejour.imageUrl} 
                    alt={sejour.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <h3 className="text-3xl font-bold text-white mb-2">{sejour.name}</h3>
                  </div>
                </div>
                
                <div className="p-6 flex-grow">
                  <p className="text-gray-700 mb-4 leading-relaxed">{sejour.description}</p>
                  
                  {/* Activities List */}
                  <div className="bg-[#afab53]/10 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-[#afab53] rounded-full"></span>
                      Activités incluses
                    </h4>
                    <ul className="space-y-2">
                      {sejour.activities.map((activity, index) => (
                        <li key={index} className="text-sm text-gray-700 flex items-start gap-2">
                          <span className="text-[#afab53] mt-1">•</span>
                          <span>{activity}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Bouton pour déplier les détails */}
                  <button 
                    onClick={() => toggleExpand(sejour.id)}
                    className="w-full py-2 mb-4 flex items-center justify-center gap-2 text-[#afab53] font-medium hover:bg-gray-50 rounded-lg transition-colors border border-dashed border-[#afab53]/30"
                  >
                    {expandedId === sejour.id ? "Masquer les détails" : "Afficher plus de détails"}
                    <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${expandedId === sejour.id ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Zone dépliante (Accordion) */}
                  <div className={`overflow-hidden transition-all duration-500 ease-in-out ${expandedId === sejour.id ? 'max-h-96 opacity-100 mb-6' : 'max-h-0 opacity-0'}`}>
                    <span>{sejour.details}</span> <br /><br />
                    <div className="p-4 bg-gray-50 rounded-lg border-l-4 border-[#afab53]">
                      <h4 className="text-sm font-bold text-gray-900 mb-2 uppercase tracking-wider">Ce que comprend le tarif :</h4>
                      <ul className="grid grid-cols-1 gap-2">
                        <li className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle2 className="w-4 h-4 text-[#afab53]" /> Logement en pension complète 
                        </li>
                        <li className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle2 className="w-4 h-4 text-[#afab53]" /> Encadrement par des guides diplômés
                        </li>
                        <li className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle2 className="w-4 h-4 text-[#afab53]" /> Navettes
                        </li>
                        <li className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle2 className="w-4 h-4 text-[#afab53]" /> Garage sécurisé
                        </li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between mb-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center gap-6 text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Users className="w-5 h-5 text-[#afab53]" />
                        <span>{sejour.targetAudience}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Clock className="w-5 h-5 text-[#afab53]" />
                        <span>{sejour.duration}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Price Block */}
                  <div className="bg-gradient-to-r from-[#afab53] to-[#8a8742] rounded-lg p-4 text-white">
                    <div className="flex items-end justify-between">
                      <div>
                        <p className="text-sm opacity-90">Tarif par personne</p>
                        <p className="text-3xl font-bold">{sejour.price.toFixed(2)} €</p>
                      </div>
                      <Link to={sejour.url}>
                        <button className="bg-white text-[#afab53] px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2">
                          <span>Nous contacter</span>
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Package */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900">Séjour personnalisé</h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Vous avez un projet spécifique ? Créez votre séjour sur mesure en choisissant parmi nos activités et nos hébergements disponibles.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <span className="text-[#afab53] text-2xl font-bold">•</span>
                  <div>
                    <h4 className="font-semibold text-gray-900">Hébergement flexible</h4>
                    <p className="text-gray-600 text-sm">Gîte d'étape ou camping selon vos besoins</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-[#afab53] text-2xl font-bold">•</span>
                  <div>
                    <h4 className="font-semibold text-gray-900">Activités variées</h4>
                    <p className="text-gray-600 text-sm">Plus de 15 activités de pleine nature au choix</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-[#afab53] text-2xl font-bold">•</span>
                  <div>
                    <h4 className="font-semibold text-gray-900">Restauration sur demande</h4>
                    <p className="text-gray-600 text-sm">Pension complète, demi-pension ou gestion libre</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-[#afab53] text-2xl font-bold">•</span>
                  <div>
                    <h4 className="font-semibold text-gray-900">Encadrement professionnel</h4>
                    <p className="text-gray-600 text-sm">Moniteurs diplômés et expérimentés</p>
                  </div>
                </div>
              </div>
              <Button className="bg-[#afab53] hover:bg-[#9a9648] text-white py-6 px-8 h-auto">
                <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5" />
                    <Link to="/contact?category=draille&stay=autre" className="text-lg">
                    Contactez-nous pour composer votre séjour
                    </Link>
                </div>
              </Button>
            </div>
            <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="/4x4_gite.jpg" 
                alt="Séjour personnalisé"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#afab53] to-[#8a8742]">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Envie d'aventures sans besoin d'un logement ?</h2>
          <p className="text-xl mb-10 text-white/90 leading-relaxed">
              Visitez notre page concernant les activités ! 
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/activites">
              <Button size="lg" className="bg-white text-[#afab53] hover:bg-gray-100 text-lg px-8 py-6">
                Activités
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Sejours;